import { useEffect, useRef } from "react";
import { settleTagRowOverflow, updateTagRowEnds } from "../lib/tag-row";

export function useTagRowOverflow<T extends HTMLElement = HTMLSpanElement>(
  updateKey: string,
  enabled = true
) {
  const rowRef = useRef<T>(null);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || !enabled) return;

    const updateOverflowState = () => settleTagRowOverflow(row);
    // A scroll moves the tags without resizing any of them, so it changes
    // only which ends still hide one.
    const updateEnds = () => updateTagRowEnds(row);

    const resizeObserver = new ResizeObserver(updateOverflowState);
    let disposed = false;
    resizeObserver.observe(row);
    // The tags too: a language switch resizes them inside a row whose own box
    // stays put, and the overflow state went stale.
    for (const tag of row.children) resizeObserver.observe(tag);
    row.addEventListener("scroll", updateEnds, { passive: true });
    updateOverflowState();
    void document.fonts.ready.then(() => {
      if (!disposed) updateOverflowState();
    });

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      row.removeEventListener("scroll", updateEnds);
    };
  }, [enabled, updateKey]);

  return rowRef;
}
